export class ElevenLabsTTS {
    constructor() {
        this.isSpeaking = false;
        this.currentAudio = null;
        this.currentAudioBlob = null;
        this.onPlayStart = null;
        this.currentText = '';
        this.abortController = null;
        this.lipSyncController = null;
        this.volumeController = null;
    }
    
    /**
     * Set the volume controller reference
     * @param {VolumeController} controller - The volume controller to use for audio volume
     */
    setVolumeController(controller) {
        this.volumeController = controller;
        console.log('Volume controller connected to TTS service');
    }

    async synthesizeSpeech(text, existingAudioBlob = null, existingAudioUrl = null) {
        try {
            // Store the current text for subtitle display
            this.currentText = text;
            
            let audioBlob;
            let audioUrl;
            
            // If existing audio blob and URL are provided, use them instead of making a new request
            if (existingAudioBlob && existingAudioUrl) {
                console.log('Using existing audio blob for replay');
                audioBlob = existingAudioBlob;
                audioUrl = existingAudioUrl;
            } else {
                // Cancel any existing request
                if (this.abortController) {
                    this.abortController.abort();
                }

                // Create new abort controller for this request
                this.abortController = new AbortController();

                // AEC (Acoustic Echo Cancellation) handles echo prevention automatically
                console.log("🎵 Starting ElevenLabs TTS - AEC will prevent echo during playback");

                const response = await fetch('/api/tts/synthesize', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ text }),
                    signal: this.abortController.signal
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                audioBlob = await response.blob();
                audioUrl = URL.createObjectURL(audioBlob);
            }

            // Clean up any existing audio
            this.stop();

            return new Promise((resolve, reject) => {
                const audio = new Audio(audioUrl);
                this.currentAudio = audio;
                
                // Apply volume control if available
                if (this.volumeController) {
                    this.volumeController.applyVolumeToAudio(audio);
                }
                
                // Store the audio blob for pause analysis
                this.currentAudioBlob = audioBlob;
                
                // Split the text into sentences
                const sentences = this.splitIntoSentences(text);
                
                // When metadata is loaded
                audio.onloadedmetadata = () => {
                    console.log('Audio metadata loaded, duration:', audio.duration);
                };

                // PRE-COORDINATION: Don't auto-start when ready, wait for lip sync
                audio.oncanplaythrough = async () => {
                    console.log('=== AUDIO READY - STARTING COORDINATION ===');
                    this.isSpeaking = true;
                    
                    // Set up audio-based pause detection if we have a lip sync controller
                    if (this.lipSyncController) {
                        try {
                            const analysisSuccessful = await this.lipSyncController.setAudioSource(audio, audioBlob);
                            if (analysisSuccessful) {
                                console.log('Audio-based pause detection enabled for lip sync');
                            } else {
                                console.log('Audio analysis not available, using phonetic-based pauses');
                            }
                        } catch (error) {
                            console.error('Error setting up audio-based pause detection:', error);
                        }
                    }
                    
                    // COORDINATION POINT: Call lip sync preparation BEFORE starting audio
                    if (this.onPlayStart) {
                        console.log('Audio ready - coordinating with lip sync before playback');
                        await this.onPlayStart(audio);
                        console.log('Lip sync coordination complete - now starting audio playback');
                    }
                    
                    // Now start audio playback after lip sync is ready
                    try {
                        await audio.play();
                        console.log('Audio playback started after lip sync coordination');
                    } catch (playError) {
                        console.error('Error starting coordinated audio playback:', playError);
                        reject(playError);
                    }
                };

                audio.onended = () => {
                    URL.revokeObjectURL(audioUrl);
                    this.isSpeaking = false;
                    this.currentAudio = null;
                    
                    // Notify animation controller (emotion controller removed
                    // in Task #38 — A2F renderer handles facial expression
                    // and decays to neutral on its own after silence).
                    if (this.animationController) {
                        this.animationController.onTTSEnd();
                        console.log('Animation controller directly notified of TTS end');
                    }
                    
                    // Dispatch an event that can be listened to by other components
                    const audioEndEvent = new CustomEvent('ttsAudioEnded', {
                        detail: { audioUrl: audioUrl }
                    });
                    document.dispatchEvent(audioEndEvent);
                    console.log('Dispatched ttsAudioEnded event for any listeners');
                    
                    resolve();
                };

                audio.onerror = (error) => {
                    URL.revokeObjectURL(audioUrl);
                    this.isSpeaking = false;
                    this.currentAudio = null;
                    reject(error);
                };

                // COORDINATION: Audio will start only after lip sync is ready
                // The oncanplaythrough event handler will coordinate and start playback
                console.log('Audio element created, waiting for coordination with lip sync');
            });
        } catch (error) {
            if (error.name === 'AbortError') {
                console.log('TTS request aborted');
            } else {
                console.error('Error synthesizing speech:', error);
            }
            throw error;
        } finally {
            this.abortController = null;
        }
    }

    stop() {
        // Abort any ongoing fetch request
        if (this.abortController) {
            this.abortController.abort();
            this.abortController = null;
        }

        // Stop current audio playback
        if (this.currentAudio) {
            try {
                // Force immediate stop by setting currentTime to end
                this.currentAudio.pause();
                this.currentAudio.currentTime = this.currentAudio.duration || 0;
                
                // Clear the source and load empty data
                this.currentAudio.src = '';
                this.currentAudio.load(); // Force reload to clear buffer
                
                // Remove from DOM if attached
                if (this.currentAudio.parentNode) {
                    this.currentAudio.remove();
                }
                
                this.currentAudio = null;
                
                // Notify all controllers about audio playback being stopped
                if (this.isSpeaking) {
                    // Notify animation controller (emotion controller removed
                    // in Task #38 — A2F renderer handles facial expression).
                    if (this.animationController) {
                        this.animationController.onTTSEnd();
                        console.log('Animation controller directly notified of TTS end from stop method');
                    }
                    
                    // Dispatch an event that can be listened to by other components
                    const audioEndEvent = new CustomEvent('ttsAudioEnded', {
                        detail: { source: 'stop_method' }
                    });
                    document.dispatchEvent(audioEndEvent);
                    console.log('Dispatched ttsAudioEnded event from stop method');
                }
            } catch (error) {
                console.error('Error stopping audio:', error);
            }
        }

        this.isSpeaking = false;
    }
    
    /**
     * Set the animation controller reference directly
     * @param {AnimationController} controller - The animation controller to notify of TTS status
     */
    setAnimationController(controller) {
        this.animationController = controller;
        console.log('Animation controller directly connected to TTS service');
    }
    
    /**
     * Set the lip sync controller reference
     * @param {LipSyncController} controller - The lip sync controller to use for audio analysis
     */
    setLipSyncController(controller) {
        this.lipSyncController = controller;
        console.log('LipSync controller connected to TTS service for audio-based pause detection');
    }
    
    /**
     * Split text into sentences for progressive display
     * @param {string} text - The text to split
     * @returns {Array} - Array of sentences
     */
    splitIntoSentences(text) {
        if (!text) return [];
        
        // Split on sentence endings (., !, ?) followed by space or end of string
        return text.split(/(?<=[.!?])\s+|(?<=[.!?])$/)
            .filter(sentence => sentence.trim().length > 0)
            .map(sentence => sentence.trim());
    }
    

}